'use client';

/**
 * RegistryStatusBadge (#6555) — compact, neutral indicator of an advocate's
 * SAK registration standing (`registry_status`/`status` on `lawyer_entities`).
 *
 * Surfaced wherever an advocate's identity or blog content appears (public
 * profile header, public blog listing + post pages) so readers — and the
 * advocate themselves — can see the current registry state.
 *
 * Standing is intentionally NOT a gate on authorship (decision on #6537/#6550):
 * advocates may publish regardless of standing; this component only SHOWS it.
 *
 * Modelled on `kancelaria/SpisStatusBadge.tsx`: a small pill with a localized
 * label + a semantic colour. `suspended`/`removed` carry a clear-but-neutral
 * warning/error tint and an icon; `active` is a subtle affirmative shown only
 * when `showActive` is set; `unknown` (and any other value) renders nothing.
 *
 * Wording for `suspended`/`removed` is consumer-protection / defamation-aware:
 * the labels mirror the official SAK register terms — `pozastavený výkon
 * advokácie` (§ 8) and `vyčiarknutý zo zoznamu advokátov` (§ 7) of zák.
 * č. 586/2003 Z. z. o advokácii. These are true, publicly-registered facts
 * stated neutrally (truth + public-register source ⇒ no difamácia per § 19 OZ /
 * § 373 TZ). Reviewed in a legal-expert pass; pending @citbaj ratification (#6555).
 */

import type { ProfessionalStatus } from '@/types/professionals-types';
import clsx from 'clsx';
import {
  AlertTriangle,
  CheckCircle,
  type LucideIcon,
  XCircle,
} from 'lucide-react';
import { useTranslations } from 'next-intl';

/** The three states this badge knows how to render. `unknown` is excluded
 *  on purpose — an unclassified registry state is not a claim we surface. */
type RegistryDisplayStatus = 'active' | 'suspended' | 'removed';

// Text uses the darker `-text` variant (not the DEFAULT) so the label clears
// WCAG 1.4.3 (4.5:1) on the translucent `/10` tint — the DEFAULT hue fails
// contrast on a light background for `warning`/`success` (#6555 review).
const STATUS_CLASSES: Record<RegistryDisplayStatus, string> = {
  active: 'bg-status-success/10 text-status-success-text',
  suspended: 'bg-status-warning/10 text-status-warning-text',
  removed: 'bg-status-error/10 text-status-error-text',
};

const STATUS_ICON: Record<RegistryDisplayStatus, LucideIcon> = {
  active: CheckCircle,
  suspended: AlertTriangle,
  removed: XCircle,
};

/**
 * Pure visibility decision (tested directly, neighbour pattern). `active`
 * only shows when the caller opts in via `showActive`; `suspended`/`removed`
 * always show; everything else (`unknown`, null, undefined) stays hidden.
 */
export function isRegistryStatusShown(
  status: ProfessionalStatus | string | null | undefined,
  showActive = false
): status is RegistryDisplayStatus {
  if (status === 'suspended' || status === 'removed') return true;
  if (status === 'active') return showActive;
  return false;
}

export function RegistryStatusBadge({
  status,
  showActive = false,
  className,
}: {
  status: ProfessionalStatus | string | null | undefined;
  /** Render a subtle affirmative pill for `active`. Off by default so the
   *  badge is silent for the common case unless a surface wants it. */
  showActive?: boolean;
  className?: string;
}): React.ReactElement | null {
  const t = useTranslations('Lawyers.registryStatus');

  // Labels mirror the official SAK register terms (§ 7 / § 8 zák. 586/2003 Z. z.)
  // — neutral statements of a public, registered fact. @citbaj to ratify (#6555).
  if (!isRegistryStatusShown(status, showActive)) return null;

  const Icon = STATUS_ICON[status];
  const label = t(status);

  return (
    <span
      className={clsx(
        'inline-flex flex-shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        STATUS_CLASSES[status],
        className
      )}
      title={label}
    >
      <Icon className='h-3.5 w-3.5' aria-hidden='true' />
      {label}
    </span>
  );
}
